import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent, GeoJsonProperties, ModalWindowComponent, SelectedLocationData } from '@ui/aos-lib';
import { KENDO_INDICATORS } from '@progress/kendo-angular-indicators';

import { SelectedLocationState } from '@portal/shared/components/listing-page/listing-page.types';
import {
  selectHasPausedReportGroup,
  selectIsReportGroupCreating,
  selectReportGroupFormData,
  selectSelectedArea,
  selectSelectedIds
} from './store/map-report-group.reducer';
import { MapReportGroupActions } from './store/map-report-group.actions';
import { MapTileComponent } from '../map/map.component';
import { MapDevicesPayload } from '../../../pages/equipment/devices/components/devices.models';
import { TranslateRegistry } from '../../../services/translate/translate.registry';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { PermissionValue, ResourceValue } from 'src/app/services/permissions/permissions.types';
import { EventsReportGroupModalComponent } from 'src/app/pages/network-monitoring/events-monitor/components/events-report-group-modal/events-report-group-modal.component';
import {
  Applicable,
  ReportGroupsCreationAPIBody
} from 'src/app/pages/administration/report-groups/report-groups.types';
import { ModalService } from 'src/app/services/utility/modal.service';
import { ListingPageId } from '../listing-page/store/listing-page-store.types';

@Component({
  selector: 'aos-map-report-group',
  imports: [
    KENDO_INDICATORS,
    TranslateModule,
    ButtonComponent,
    MapTileComponent,
    ModalWindowComponent,
    EventsReportGroupModalComponent
  ],
  template: `
    <ui-modal-window
      [windowTitleText]="modalTitleText | translate"
      (closeButtonClick)="cancelReportGroupCreation()"
    >
      <aos-events-report-group-modal
        [editReportGroupsFormData]="reportGroupFormData()"
        (cancelButtonClick)="cancelReportGroupCreation()"
        (closeWithRefresh)="closeModal()"
        (create)="onSubmit($event)"
      />
    </ui-modal-window>
    <aos-map
      [filtersPayload]="filtersPayload()"
      [chartSegmentsColors]="chartSegmentsColors()"
      [createBadgesFn]="createBadgesFn()"
      [selectedLocationData]="selectedLocationData()"
      [locationSelectedFn]="locationSelectedFn()!"
      [polygonEnabled]="true"
      [isPolygonDrawActive]="false"
      (areaSelected)="onAreaSelected($event)"
    ></aos-map>
    @if (selectedDevices().length; as selectedCount) {
      <section class="aos-devices-map__selected-area">
        <div class="aos-devices-map__selected-area-count">
          <span> {{ selectedCount }} {{ selectedText | translate }} </span>
        </div>
        @if (hasReportGroupCreatePermission()) {
          <ui-button
            size="medium"
            themeColor="text"
            [iconName]="createReportGroupIconName()"
            (clicked)="openModal()"
          >
            {{ createReportGroupText() | translate }}
          </ui-button>
        }

        <ui-button
          iconName="close"
          [size]="discardReportGroupIconName()"
          (click)="clearSelectedArea()"
        >
          @if (hasPausedReportGroup()) {
            <span> {{ discardReportGroupText | translate }}</span>
          }
        </ui-button>
      </section>
    }

    @if (isReportGroupCreating()) {
      <div class="aos-devices-map__backdrop">
        <kendo-loader
          [type]="'pulsing'"
          [themeColor]="'primary'"
          [size]="'medium'"
        >
        </kendo-loader>
      </div>
    }
  `,
  styleUrl: './map-report-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DevicesMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild(ModalWindowComponent, { static: true })
  modalWindowComponent!: ModalWindowComponent;
  @ViewChild(MapTileComponent, { static: true })
  mapComponent!: MapTileComponent;

  selectedLocationData = input<SelectedLocationState>({ selectedLocation: null, sidePanelIds: [] });
  locationSelectedFn = input.required<(selectedLocationData: SelectedLocationData | null) => void>();
  filtersPayload = input.required<MapDevicesPayload>();
  chartSegmentsColors = input.required<{ [health: string]: string }>();
  createBadgesFn = input.required<(properties: GeoJsonProperties, badgeContainerRef: ViewContainerRef) => string>();
  applicable = input(Applicable.NetworkMonitoring);
  actionSource = input.required<string>();
  pageId = input<ListingPageId>();

  readonly #store = inject(Store);
  readonly #permissions = inject(PermissionsService);
  readonly #modalService = inject(ModalService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly hasReportGroupCreatePermission = toSignal(
    this.#permissions.hasRight(ResourceValue.REPORT_GROUPS, PermissionValue.CREATE)
  );
  protected readonly selectedArea = this.#store.selectSignal(selectSelectedArea);
  protected readonly isAreaSelected = computed(() => this.selectedArea().length > 0);
  protected readonly selectedDevices = this.#store.selectSignal(selectSelectedIds);
  protected readonly reportGroupFormData = this.#store.selectSignal(selectReportGroupFormData);
  protected readonly hasPausedReportGroup = this.#store.selectSignal(selectHasPausedReportGroup);
  protected readonly isReportGroupCreating = this.#store.selectSignal(selectIsReportGroupCreating);
  protected readonly createReportGroupText = computed(() =>
    this.hasPausedReportGroup() ? this.applyChangesText : this.createReportGroupTitle
  );
  protected readonly createReportGroupIconName = computed(() => (this.hasPausedReportGroup() ? 'checked' : 'plus'));
  protected readonly discardReportGroupIconName = computed(() => (this.hasPausedReportGroup() ? 'medium' : 'small'));
  protected readonly onPayloadChange = effect(() => {
    if (this.selectedArea().length && this.filtersPayload().filters?.length) {
      this.#store.dispatch(
        MapReportGroupActions.fetchDevicesForSelectedArea({
          filters: this.filtersPayload().filters![0],
          search: this.filtersPayload().search,
          source: this.actionSource()
        })
      );
    }
  });
  protected readonly onApplicableChange = effect(() => {
    if (this.applicable()) {
      this.#store.dispatch(
        MapReportGroupActions.updateInitialApplicable({
          applicable: this.applicable()
        })
      );
    }
  });

  protected readonly createReportGroupTitle = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.CREATE_REPORT_GROUP;
  protected readonly applyChangesText =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.APPLY_CHANGES_TO_REPORT_GROUP;
  protected readonly discardReportGroupText =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.DISCARD_REPORT_GROUP;
  protected readonly selectedText = TranslateRegistry.get.FILTER.COMMON.SELECTED;
  protected readonly modalTitleText = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.CREATE_REPORT_GROUP;

  ngOnDestroy(): void {
    this.#store.dispatch(MapReportGroupActions.cancelReportGroupCreation());
  }

  ngAfterViewInit(): void {
    this.#modalService.register(this.modalWindowComponent, this.#destroyRef);
  }

  protected onAreaSelected(area: [number, number][]): void {
    if (area.length) {
      this.#store.dispatch(MapReportGroupActions.updateSelectedArea({ area }));
    } else {
      this.#store.dispatch(MapReportGroupActions.cancelReportGroupCreation());
    }
  }

  protected openModal(): void {
    this.modalWindowComponent.open({
      ...(this.#modalService.containerRef() && { appendTo: this.#modalService.containerRef()! })
    });
    this.#store.dispatch(MapReportGroupActions.toggleReportGroupCreation({ paused: false }));
  }

  protected clearSelectedArea(): void {
    this.#store.dispatch(MapReportGroupActions.cancelReportGroupCreation());
    this.clearPolygon();
  }

  protected closeModal(): void {
    this.#store.dispatch(MapReportGroupActions.toggleReportGroupCreation({ paused: true }));
    this.clearPolygon();

    if (this.modalWindowComponent) {
      this.modalWindowComponent.close();
    }
  }

  protected onSubmit(payload: ReportGroupsCreationAPIBody): void {
    this.#store.dispatch(
      MapReportGroupActions.createReportGroup({ payload, source: this.actionSource(), pageId: this.pageId() })
    );

    if (this.modalWindowComponent) {
      this.modalWindowComponent.close();
    }
  }

  protected cancelReportGroupCreation(): void {
    this.clearSelectedArea();
    this.modalWindowComponent.close();
  }

  private clearPolygon(): void {
    this.mapComponent.uiMapService.clearPolygon();
  }
}
